import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Wallet, Target, TrendingDown, TrendingUp, ShieldAlert, Loader2, FileText, ArrowRight, AlertTriangle, MousePointerClick, Users, ChevronDown, ChevronUp, Hourglass } from 'lucide-react';
import Button from '../ui/Button';
import { Card } from '../ui/Card';
import { PacingChart, MetricChart } from './tmCharts';
import ExpectedCard from './ExpectedCard';
import { ActionRow, RichText, SectionTitle, useFmt } from './tmShared';
import type { DayPoint, Evolution, RunAction, TrafficManager, TrafficRun } from '../../services/trafficManagerService';

/** Variação com sinal e direção boa/ruim explícitas (ícone + texto, não só cor). */
const Delta: React.FC<{ before: number | null; after: number | null; lowerIsBetter?: boolean }> = ({ before, after, lowerIsBetter }) => {
  const { t } = useTranslation();
  if (before == null || after == null || before === 0) return null;
  const pct = ((after - before) / before) * 100;
  if (Math.abs(pct) < 1) return <span className="text-gray-500">{t('trafficManager.overview.stable', 'estável')}</span>;
  const good = lowerIsBetter ? pct < 0 : pct > 0;
  const Icon = pct < 0 ? TrendingDown : TrendingUp;
  return (
    <span className={`inline-flex items-center gap-0.5 font-medium ${good ? 'text-emerald-700 dark:text-emerald-400' : 'text-red-700 dark:text-red-400'}`}>
      <Icon className="w-3.5 h-3.5" />{pct > 0 ? '+' : ''}{pct.toFixed(0)}%
      <span className="sr-only">{good ? t('trafficManager.overview.better', 'melhor') : t('trafficManager.overview.worse', 'pior')}</span>
    </span>
  );
};

const Tile: React.FC<{ icon: React.ElementType; label: string; value: React.ReactNode; sub?: React.ReactNode; children?: React.ReactNode }> = ({ icon: Icon, label, value, sub, children }) => (
  <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/60 p-4 min-w-0">
    <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400"><Icon className="w-4 h-4 shrink-0" /><span className="truncate">{label}</span></div>
    <div className="mt-1 text-2xl font-bold text-gray-900 dark:text-white tabular-nums truncate">{value}</div>
    {children}
    {sub && <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">{sub}</div>}
  </div>
);

const soma = (dias: DayPoint[]) => dias.reduce((a, d) => ({ spend: a.spend + d.spend, clicks: a.clicks + d.clicks, imp: a.imp + d.impressions, conv: a.conv + d.conversions }), { spend: 0, clicks: 0, imp: 0, conv: 0 });

/** Aprovações agrupadas por campanha, compactas: as 3 primeiras abertas, o resto sob "ver todas". */
const PendingCard: React.FC<{
  items: { run: TrafficRun; a: RunAction }[];
  cur: string;
  busyRun: string | null;
  onDecide: (runId: string, actionId: string | undefined, d: 'approve' | 'reject') => void;
}> = ({ items, cur, busyRun, onDecide }) => {
  const { t } = useTranslation();
  const [all, setAll] = useState(false);
  const shown = all ? items : items.slice(0, 3);
  const grupos: [string, typeof items][] = [];
  for (const it of shown) {
    const k = it.a.campaign_name || t('trafficManager.overview.account', 'Conta');
    const g = grupos.find(([n]) => n === k);
    if (g) g[1].push(it); else grupos.push([k, [it]]);
  }
  return (
    <Card className="border-amber-200 dark:border-amber-800">
      <SectionTitle icon={ShieldAlert} right={items.length > 1 && (
        <Button size="sm" disabled={!!busyRun} onClick={() => [...new Set(items.map((p) => p.run.id))].forEach((id) => onDecide(id, undefined, 'approve'))}>
          {t('trafficManager.approveAll', 'Aprovar todas ({{n}})', { n: items.length })}
        </Button>
      )}>
        {t('trafficManager.overview.pendingCount', '{{n}} ação(ões) aguardando você', { n: items.length })}
      </SectionTitle>
      <p className="text-sm text-gray-500 -mt-2 mb-1">{t('trafficManager.overview.pendingHelp', 'Nada muda nas contas até você aprovar. Ao aprovar, as travas conferem de novo com os números de agora.')}</p>
      {grupos.map(([nome, lista]) => (
        <div key={nome} className="mt-3">
          <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide truncate">{nome}</div>
          <div className="divide-y divide-gray-100 dark:divide-gray-700">
            {lista.map(({ run, a }) => (
              <ActionRow key={a.id} a={{ ...a, campaign_name: null }} cur={cur} busy={busyRun === run.id} onApprove={() => onDecide(run.id, a.id, 'approve')} onReject={() => onDecide(run.id, a.id, 'reject')} />
            ))}
          </div>
        </div>
      ))}
      {items.length > 3 && (
        <button type="button" onClick={() => setAll((v) => !v)} className="mt-2 inline-flex items-center gap-1 text-sm text-primary-600 hover:underline">
          {all ? <><ChevronUp className="w-4 h-4" />{t('trafficManager.overview.showLess', 'Mostrar menos')}</> : <><ChevronDown className="w-4 h-4" />{t('trafficManager.overview.showAll', 'Ver todas ({{n}})', { n: items.length })}</>}
        </button>
      )}
    </Card>
  );
};

const OverviewTab: React.FC<{
  manager: TrafficManager;
  evolution: Evolution | null;
  runs: TrafficRun[];
  busyRun: string | null;
  onDecide: (runId: string, actionId: string | undefined, d: 'approve' | 'reject') => void;
  onOpenRuns: () => void;
}> = ({ manager, evolution, runs, busyRun, onDecide, onOpenRuns }) => {
  const { t } = useTranslation();
  const f = useFmt();
  const cur = manager.currency || 'BRL';
  const r = manager.snapshot?.ritmo;
  const placar = evolution?.placar;
  const comp = evolution?.comparativo;
  const pendentes = runs.flatMap((run) => (run.actions || []).filter((a) => a.status === 'proposed').map((a) => ({ run, a })));
  const ultima = runs.find((x) => x.trigger !== 'chat');
  const diaria = evolution?.diaria || [];
  const ultimos30 = diaria.slice(-30);
  const acoes = evolution?.acoes || [];

  // Últimos 7 dias fechados x os 7 anteriores (sempre há dado, ao contrário do antes x depois).
  const s7 = soma(diaria.slice(-7));
  const p7 = soma(diaria.slice(-14, -7));
  const cpa7 = s7.conv ? s7.spend / s7.conv : null; const cpaP = p7.conv ? p7.spend / p7.conv : null;
  const cpc7 = s7.clicks ? s7.spend / s7.clicks : null; const cpcP = p7.clicks ? p7.spend / p7.clicks : null;
  const ctr7 = s7.imp ? (s7.clicks / s7.imp) * 100 : 0;

  const pct = r && r.verba_mensal ? Math.min(100, (r.gasto_no_mes / r.verba_mensal) * 100) : 0;
  const ideal = r && r.dias_no_mes ? Math.min(100, ((r.dias_no_mes - r.dias_restantes) / r.dias_no_mes) * 100) : 0;
  const lbl7 = t('trafficManager.overview.last7', 'últimos 7 dias');

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Tile icon={Wallet} label={t('trafficManager.overview.spentMonth', 'Gasto no mês')} value={r ? f.money(r.gasto_no_mes, cur) : '—'}
          sub={r ? t('trafficManager.overview.ofBudget', 'de {{v}} · {{d}} dia(s) restantes', { v: f.money(r.verba_mensal, cur), d: r.dias_restantes }) : null}>
          {r && (
            <div className="relative mt-2 h-1.5 rounded-full bg-gray-100 dark:bg-gray-700" title={t('trafficManager.overview.idealMarker', 'O traço marca onde o gasto deveria estar hoje') as string}>
              <div className={`h-full rounded-full ${r.verba_esgotada ? 'bg-red-500' : 'bg-primary-600'}`} style={{ width: `${pct}%` }} />
              <div className="absolute -top-1 -bottom-1 w-0.5 bg-gray-900/70 dark:bg-white/80" style={{ left: `${ideal}%` }} />
            </div>
          )}
        </Tile>
        <Tile icon={Target} label={t('trafficManager.overview.cpa7', 'Custo por conversão')} value={cpa7 != null ? f.money(cpa7, cur) : '—'}
          sub={<span className="inline-flex items-center gap-1.5">{lbl7} <Delta before={cpaP} after={cpa7} lowerIsBetter /></span>} />
        <Tile icon={Users} label={t('trafficManager.metrics.conversions', 'Conversões')} value={f.int(s7.conv)}
          sub={<span className="inline-flex items-center gap-1.5">{lbl7} <Delta before={p7.conv || null} after={s7.conv} /></span>} />
        <Tile icon={MousePointerClick} label={t('trafficManager.metrics.cpc', 'CPC')} value={cpc7 != null ? f.money(cpc7, cur) : '—'}
          sub={<span className="inline-flex items-center gap-1.5">{t('trafficManager.overview.ctrShort', 'CTR {{v}}', { v: `${f.dec(ctr7, 2)}%` })} · <Delta before={cpcP} after={cpc7} lowerIsBetter /></span>} />
      </div>

      {pendentes.length > 0 && <PendingCard items={pendentes} cur={cur} busyRun={busyRun} onDecide={onDecide} />}

      {evolution?.esperado && <ExpectedCard e={evolution.esperado} cur={cur} />}

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 sm:gap-6">
        <Card className="xl:col-span-2">
          <SectionTitle icon={Wallet}>{t('trafficManager.overview.pacingTitle', 'Verba do mês: real x ritmo ideal')}</SectionTitle>
          {evolution ? <PacingChart data={evolution.mes} cur={cur} budget={parseFloat(String(manager.monthly_budget)) || 0} /> : <div className="h-56 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>}
          {r && (
            <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
              <div><div className="text-xs text-gray-500">{t('trafficManager.overview.ceiling', 'Teto por dia até o fim do mês')}</div><div className="font-semibold text-gray-900 dark:text-white tabular-nums">{f.money(r.teto_diario, cur)}</div></div>
              <div><div className="text-xs text-gray-500">{t('trafficManager.overview.runningBudgets', 'Orçamentos rodando hoje')}</div><div className="font-semibold text-gray-900 dark:text-white tabular-nums">{f.money(r.soma_orcamentos_diarios_ativos, cur)}</div></div>
              <div className="col-span-2 sm:col-span-1"><div className="text-xs text-gray-500">{t('trafficManager.overview.vsPace', 'Em relação ao ritmo ideal')}</div><div className="font-semibold text-gray-900 dark:text-white tabular-nums">{r.desvio_do_ritmo > 0 ? '+' : '−'}{f.money(Math.abs(r.desvio_do_ritmo), cur)}</div></div>
            </div>
          )}
          {r && r.soma_orcamentos_diarios_ativos > r.teto_diario_com_folga && (
            <p className="mt-3 text-xs text-amber-800 dark:text-amber-300 flex gap-1.5"><AlertTriangle className="w-4 h-4 shrink-0" />
              {t('trafficManager.overview.overWarn', 'Os orçamentos diários somados ({{s}}) passam do teto que cabe na verba ({{c}}/dia). O robô corta para voltar ao ritmo.', { s: f.money(r.soma_orcamentos_diarios_ativos, cur), c: f.money(r.teto_diario, cur) })}
            </p>
          )}
        </Card>
        <Card className="flex flex-col">
          <SectionTitle icon={FileText} right={<button className="text-xs text-primary-600 hover:underline" onClick={onOpenRuns}>{t('trafficManager.overview.seeRuns', 'Ver rodadas')}</button>}>
            {t('trafficManager.overview.lastReport', 'Último relatório')}
          </SectionTitle>
          {!ultima && <p className="text-sm text-gray-500">{t('trafficManager.noRuns', 'Nenhuma rodada ainda. Clique em "Rodar agora" para a primeira análise.')}</p>}
          {ultima && ultima.status === 'running' && <p className="text-sm text-gray-600 dark:text-gray-300 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />{t('trafficManager.analyzing', 'Analisando as campanhas…')}</p>}
          {ultima && ultima.status !== 'running' && (
            <>
              <div className="text-xs text-gray-500 mb-2">{f.dateTime(ultima.created_at)}</div>
              <RichText text={ultima.summary || ultima.error} clamp className="text-sm text-gray-700 dark:text-gray-300" />
              <button type="button" onClick={onOpenRuns} className="mt-3 self-start inline-flex items-center gap-1 text-sm text-primary-600 hover:underline">{t('trafficManager.overview.readFull', 'Ler completo')} <ArrowRight className="w-3.5 h-3.5" /></button>
            </>
          )}
        </Card>
      </div>

      {comp ? (
        <Card>
          <SectionTitle icon={TrendingUp}>{t('trafficManager.overview.beforeAfter', 'Antes x depois do robô')}</SectionTitle>
          <p className="text-xs text-gray-500 -mt-2 mb-3">{t('trafficManager.overview.beforeAfterHelp', 'Média diária dos 14 dias antes da primeira rodada ({{d}}) contra os últimos 14 dias fechados. Inclui sazonalidade: leia junto com o efeito de cada mudança na aba Aprendizado.', { d: f.day(comp.desde) })}</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {([
              [t('trafficManager.metrics.cpa', 'Custo/conv.'), comp.antes.cpa, comp.depois.cpa, true, 'money'],
              [t('trafficManager.metrics.convPerDay', 'Conv./dia'), comp.antes.conversoes_por_dia, comp.depois.conversoes_por_dia, false, 'dec'],
              [t('trafficManager.metrics.cpc', 'CPC'), comp.antes.cpc, comp.depois.cpc, true, 'money'],
              [t('trafficManager.metrics.ctr', 'CTR'), comp.antes.ctr, comp.depois.ctr, false, 'pct']
            ] as [string, number | null, number | null, boolean, string][]).map(([l, a, d, low, kind]) => {
              const fmt = (v: number | null) => (v == null ? '—' : kind === 'money' ? f.money(v, cur) : kind === 'pct' ? `${f.dec(v, 2)}%` : f.dec(v, 2));
              return (
                <div key={l}>
                  <div className="text-xs text-gray-500">{l}</div>
                  <div className="text-lg font-semibold text-gray-900 dark:text-white tabular-nums">{fmt(d)}</div>
                  <div className="text-xs text-gray-500 flex items-center gap-1.5">{fmt(a)} <ArrowRight className="w-3 h-3" /> <Delta before={a} after={d} lowerIsBetter={low} /></div>
                </div>
              );
            })}
          </div>
        </Card>
      ) : evolution && (
        <div className="flex items-start gap-3 rounded-xl border border-dashed border-gray-300 dark:border-gray-600 p-4 text-sm text-gray-600 dark:text-gray-300">
          <Hourglass className="w-5 h-5 text-sky-600 shrink-0" />
          <div>
            <div className="font-medium text-gray-900 dark:text-white">{t('trafficManager.overview.beforeAfter', 'Antes x depois do robô')}</div>
            {t('trafficManager.overview.beforeAfterWaiting', 'Aparece quando houver 7 dias fechados desde a primeira rodada: resultado de mídia precisa de tempo para maturar, e comparar antes disso mostraria ruído.')}
            {placar && placar.maturando > 0 && <span> {t('trafficManager.overview.maturingNow', '{{n}} mudança(s) maturando agora.', { n: placar.maturando })}</span>}
          </div>
        </div>
      )}

      {evolution && (
        <div>
          <h2 className="font-semibold text-gray-900 dark:text-white mb-1">{t('trafficManager.overview.evolution', 'Evolução das campanhas (30 dias)')}</h2>
          <p className="text-xs text-gray-500 mb-3">{t('trafficManager.overview.evolutionHelp', 'Dias fechados. As linhas tracejadas marcam os dias em que o robô mexeu nas campanhas; passe o mouse para ver o quê.')}</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <MetricChart title={t('trafficManager.metrics.cpa', 'Custo/conv.')} data={ultimos30} metric="cpa" cur={cur} actions={acoes} headline={cpa7 != null ? f.money(cpa7, cur) : '—'} headlineLabel={lbl7} />
            <MetricChart title={t('trafficManager.metrics.conversions', 'Conversões')} data={ultimos30} metric="conversions" kind="bar" cur={cur} actions={acoes} headline={f.int(s7.conv)} headlineLabel={lbl7} />
            <MetricChart title={t('trafficManager.metrics.cpc', 'CPC')} data={ultimos30} metric="cpc" cur={cur} actions={acoes} headline={cpc7 != null ? f.money(cpc7, cur) : '—'} headlineLabel={lbl7} />
            <MetricChart title={t('trafficManager.metrics.spend', 'Gasto')} data={ultimos30} metric="spend" kind="bar" cur={cur} actions={acoes} headline={f.money(s7.spend, cur)} headlineLabel={lbl7} />
          </div>
        </div>
      )}
    </div>
  );
};

export default OverviewTab;
